import React from "react";
import PropTypes from "prop-types";
import useMediaQuery from "@mui/material/useMediaQuery";
import ListSubheader from "@mui/material/ListSubheader";
import { useTheme } from "@mui/material/styles";
import { List } from "react-window";

//-- Shared react-window adapter for MUI Autocomplete's ListboxComponent.
//-- Rendering large option lists resulted in slow load times; react-window
//-- virtualization improves the load time significantly.
//-- Per-usage config (itemSize, childSize, listboxPadding, listStyle) is
//-- passed through the Autocomplete `ListboxProps` prop.
//-- Pattern: https://mui.com/material-ui/react-autocomplete/#virtualization

export const LISTBOX_BORDER_STYLE = {
  borderStyle: "solid",
  borderColor: "#F7F9FA",
  borderWidth: 2,
};

//-- Module scope: rowComponent identity must be stable or List remounts
//-- every row on each render.
function RowComponent({ index, style, itemData, listboxPadding }) {
  return React.cloneElement(itemData[index], {
    style: {
      ...style,
      //-- react-window positions rows via transform; top composes additively
      //-- to offset rows below the listbox's top padding.
      top: (style.top ?? 0) + listboxPadding,
    },
  });
}

RowComponent.propTypes = {
  index: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired,
  itemData: PropTypes.array.isRequired,
  listboxPadding: PropTypes.number.isRequired,
};

const VirtualizedListbox = React.forwardRef(function VirtualizedListbox(
  props,
  ref,
) {
  const {
    children,
    itemSize: itemSizeProp,
    childSize,
    listboxPadding = 8,
    listStyle,
    className,
    style,
    ...other //-- role="listbox", id, onMouseDown, aria-* from Autocomplete
  } = props;

  //-- Bridge MUI's listbox ref to the scrolling root element so
  //-- useAutocomplete's scrollTop management (e.g. reset to top when typing
  //-- filters the options) targets the real scroller.
  const setListboxRef = React.useCallback(
    api => {
      const node = api ? api.element : null;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = childSize ?? itemSizeProp ?? (smUp ? 36 : 48);

  const getChildSize = child => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 48;
    }
    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  return (
    <List
      className={className}
      listRef={setListboxRef}
      rowComponent={RowComponent}
      rowCount={itemCount}
      rowHeight={index => getChildSize(itemData[index])}
      rowProps={{ itemData, listboxPadding }}
      overscanCount={5}
      tagName="ul"
      style={{
        ...style,
        height: getHeight() + 2 * listboxPadding,
        width: "100%",
        ...listStyle,
      }}
      {...other}
    />
  );
});

VirtualizedListbox.propTypes = {
  children: PropTypes.node,
  itemSize: PropTypes.number,
  childSize: PropTypes.number,
  listboxPadding: PropTypes.number,
  listStyle: PropTypes.object,
};

export default VirtualizedListbox;
